import { View, StyleSheet, Platform } from "react-native";
import BlankLayout from "../layouts/BlankLayout";
import LoginFormContainer from "../containers/auth/LoginFormContainer/LoginFormContainer";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import { useAuth } from "../hooks/useAuth";
import { NavigationProp, RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { useAppConfig } from "../AppConfigProvider";
import { useEffect, useMemo, useState, useCallback } from "react";
import type { RootStackParamList } from "../navigation/navigation.types";
import { getDefaultAuthenticatedRoute } from "../utils/authRoutes.utils";
import { UserRoleUI } from "@axio/auth-guard";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import Loader from "../components/common/status/Loader/Loader";
import UnicornStudio from "unicornstudio-react";
import { colors, spacing } from "../styles/design-system";

// Bundled as static asset so /static/media/... is served as JSON (avoids SPA fallback returning HTML)
const auroraSceneJsonUrl = require("../web/unicorn-aurora.json") as string;

function LoginView(isLMS = false) {
  const { isLarge } = useResponsiveScreen();
  const { isAuthenticated, isEmailVerified, otpVerifiedForSession, userInfo } = useAuth();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, "LoginSuperAdmin">>();
  const { isCompanionMode, isLTI, ltiLaunchUrl, isConfigReady } = useAppConfig();

  const isSuperAdminRoute = route.name === "LoginSuperAdmin";
  const shouldRedirectToLms = Boolean(isLTI && !isSuperAdminRoute);
  const redirectTarget = useMemo(() => ltiLaunchUrl ?? null, [ltiLaunchUrl]);
  const [auroraSceneError, setAuroraSceneError] = useState(false);
  const handleAuroraError = useCallback(() => setAuroraSceneError(true), []);

  useEffect(() => {
    if (isAuthenticated && isEmailVerified && otpVerifiedForSession && isConfigReady) {
      const redirect = getDefaultAuthenticatedRoute({
        userRole: userInfo?.userRole ?? UserRoleUI.STUDENT,
        isCompanionMode,
      });
      navigation.navigate(redirect);
    }
  }, [isAuthenticated, isEmailVerified, otpVerifiedForSession, navigation, isCompanionMode, userInfo?.userRole]);

  useEffect(() => {
    if (!shouldRedirectToLms || !redirectTarget) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const timeout = window.setTimeout(() => {
      window.location.href = redirectTarget;
    }, 1500);

    return () => window.clearTimeout(timeout);
  }, [redirectTarget, shouldRedirectToLms]);

  if (shouldRedirectToLms) {
    return (
      <BlankLayout>
        <View style={styles.fullScreenCenter}>
          <View style={styles.redirectContainer}>
            <CustomText
              role="header"
              text="This tenant uses LMS sign-in."
              size="l"
              textType="display"
              weight="bold"
              useTranslationText={false}
              style={styles.textCenter}
            />
            <CustomSpacing size="s" />
            <CustomText
              role="header"
              text={
                redirectTarget
                  ? "We will redirect you back to your LMS to relaunch Axio."
                  : "Return to your LMS to relaunch Axio."
              }
              size="m"
              weight="regular"
              useTranslationText={false}
              style={styles.textCenter}
            />
            <CustomSpacing size="l" />
            {redirectTarget ? <Loader /> : null}
          </View>
        </View>
      </BlankLayout>
    );
  }

  return (
    <BlankLayout>
      <View style={styles.container}>
        {/* WebGL Background */}
        {Platform.OS === "web" && !auroraSceneError && (
          <View style={[StyleSheet.absoluteFill, styles.auroraBackground]} pointerEvents="auto">
            <UnicornStudio
              jsonFilePath={auroraSceneJsonUrl}
              width="100%"
              height="100%"
              scale={1}
              dpi={1}
              lazyLoad={false}
              onError={handleAuroraError}
            />
          </View>
        )}

        {/* Main Content */}
        <View style={[styles.contentContainer, !isLarge && styles.contentContainerMobile]}>
          <LoginFormContainer isSuperAdminRoute={isSuperAdminRoute} />
        </View>
      </View>
    </BlankLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.grey[50],
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
  },
  fullScreenCenter: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  contentContainer: {
    width: "100%",
    zIndex: 10,
    padding: spacing.sm,
  },
  contentContainerMobile: {
    maxWidth: "100%",
    padding: spacing.sm,
  },
  redirectContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: spacing.lg,
    maxWidth: 520,
    alignSelf: "center",
  },
  textCenter: {
    textAlign: "center",
  },
  auroraBackground: {
    zIndex: 0,
  },
});

export default LoginView;
